import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaHotel, FaTools, FaLandmark, FaUsers,
  FaUmbrellaBeach, FaUserPlus, FaSignInAlt,
  FaTimes, FaBuilding, FaSignOutAlt, FaBed,
  FaChevronRight, FaCompass, FaCalendarCheck, FaMoon, FaSun
} from 'react-icons/fa';
import { FiUser } from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import { GiHouseKeys } from 'react-icons/gi';
import { MdOutlineRealEstateAgent } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import homeStyles from '../home/ServiceSelector.module.css';
import styles from './PremiumHeader.module.css';
import LogoWhite from '../../assets/logo white.png';
import LogoBlack from '../../assets/logo black.jpg';
import LanguageSelector from '../LanguageSelector';
import { logoutUser } from '../../api/user/auth';
import { getUserProfile } from '../../api/user/account';
import defaultProfile from '../account-billing/account/default-profile.jpg';
import { useTheme } from '../../context/ThemeContext';
import NotificationBell from '../notification/NotificationBell';
import DashboardShortcuts from './DashboardShortcuts';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const PremiumHeader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const logoSrc = theme === 'dark' ? LogoWhite : LogoBlack;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [token, setToken] = useState(getCookie('is_logged_in') === 'true');
  const [userName, setUserName] = useState('User');
  const [profileImage, setProfileImage] = useState(defaultProfile);
  const [profileUser, setProfileUser] = useState(null);
  const mobileMenuRef = useRef(null);

  const allServices = [
    {
      id: 'hotel', tab: 'stay', title: t('services.book_hotels.title'),
      description: t('services.book_hotels.description'), route: '/hotels',
      icon: <FaHotel />, accent: '#0f7bcd'
    },
    {
      id: 'guest-house', tab: 'stay', title: t('services.guest_houses.title'),
      description: t('services.guest_houses.description'), route: '/guest-house',
      icon: <IoBedOutline />, accent: '#10b981'
    },
    {
      id: 'rent', tab: 'stay', title: t('services.rent_properties.title'),
      description: t('services.rent_properties.description'), route: '/rent',
      icon: <GiHouseKeys />, accent: '#f59e0b'
    },
    {
      id: 'buy', tab: 'stay', title: t('services.buy_property.title'),
      description: t('services.buy_property.description'), route: '/buy',
      icon: <MdOutlineRealEstateAgent />, accent: '#d69e2e'
    },
    {
      id: 'coliving', tab: 'stay', title: t('services.coliving.title'),
      description: t('services.coliving.description'), route: '/coliving',
      icon: <FaUsers />, accent: '#764ba2'
    },
    {
      id: 'land', tab: 'stay', title: t('services.land.title'),
      description: t('services.land.description'), route: '/land',
      icon: <FaLandmark />, accent: '#16a34a'
    },
    {
      id: 'services', tab: 'services', title: t('services.service_providers.title'),
      description: t('services.service_providers.description'), route: '/serviceProvider',
      icon: <FaTools />, accent: '#0f7bcd'
    },
    {
      id: 'vacation', tab: 'services', title: t('services.vacation_rentals.title'),
      description: t('services.vacation_rentals.description'), route: '/comingSoon',
      icon: <FaUmbrellaBeach />, accent: '#10b981'
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    const handleCookieCheck = () => setToken(getCookie('is_logged_in') === 'true');

    document.addEventListener('mousedown', handleClickOutside);
    handleCookieCheck();

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load the logged-in user's profile (name + avatar) for the mobile menu
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUserProfile();
        if (response.success) {
          setProfileUser(response.user);
          const fullName = `${response.user.first_name || ''} ${response.user.last_name || ''}`.trim();
          setUserName(fullName || 'User');
          if (response.user.profile && response.user.profile !== 'default-profile.jpg') {
            setProfileImage(`${response.user.profile}`);
          } else {
            setProfileImage(defaultProfile);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        setProfileImage(defaultProfile);
      }
    };

    if (getCookie('is_logged_in') === 'true') {
      fetchUserData();
    }
  }, []);

  const handleServiceSelect = (route) => {
    navigate(route);
    setIsMobileMenuOpen(false);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutConfirm(false);
    try {
      const response = await logoutUser();
      console.log(response);
      if (response) {
        document.cookie = "is_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        setToken(false);
        navigate('/login', { replace: true });
        setIsMobileMenuOpen(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    setShowLogoutConfirm(true);
  };

  // Close logout modal on Escape
  useEffect(() => {
    if (!showLogoutConfirm) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowLogoutConfirm(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showLogoutConfirm]);

  return (
    <>
      <header className={`${homeStyles.header} ${homeStyles.headerScrolled} ${styles.solid}`}>
        <div className={homeStyles.headerContent}>
          {/* Brand */}
          <Link to='/' className={homeStyles.brand} aria-label="Spotfli">
            <img src={logoSrc} alt="Spotfli" className={homeStyles.brandLogo} />
          </Link>

          {/* Navigation */}
          <nav className={homeStyles.navMenu} aria-label="Main navigation">
            <button onClick={() => handleServiceSelect('/')} className={homeStyles.navLink}>
              {t('options.services')}
            </button>
            <button onClick={() => handleServiceSelect('/hotels')} className={homeStyles.navLink}>
              {t('options.hotel')}
            </button>
            <button onClick={() => handleServiceSelect('/rent')} className={homeStyles.navLink}>
              {t('options.rent')}
            </button>
            <button onClick={() => handleServiceSelect('/buy')} className={homeStyles.navLink}>
              {t('options.buy')}
            </button>
          </nav>

          {/* Actions */}
          <div className={homeStyles.navActions}>
            <button
              className={homeStyles.themeToggle}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? <FaSun /> : <FaMoon />}
            </button>
            <div className={homeStyles.language}><LanguageSelector /></div>
            {token ? (
              <div className={homeStyles.userMenu}>
                <NotificationBell />
                <DashboardShortcuts user={profileUser} />
                <button onClick={() => navigate('/user/bookings')} className={homeStyles.avatarBtn} title={t('home.bottom_nav.bookings')}>
                  <FaCalendarCheck />
                </button>
                <button onClick={() => navigate('/accountbilling')} className={homeStyles.avatarBtn} title={t('sidebar.manage_account')}>
                  <FiUser />
                </button>
                <button onClick={handleLogout} className={homeStyles.avatarBtn} title={t('service_selector.log_out')}>
                  <FaSignOutAlt />
                </button>
              </div>
            ) : (
              <button onClick={() => navigate('/login')} className={homeStyles.loginBtn}>
                <span>{t('service_selector.login')}</span>
              </button>
            )}
          </div>

          <button
            className={`${homeStyles.mobileMenuBtn} ${isMobileMenuOpen ? homeStyles.active : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      <div ref={mobileMenuRef} className={`${homeStyles.mobileMenu} ${isMobileMenuOpen ? homeStyles.open : ''}`}>
        <div className={homeStyles.mobileMenuHeader}>
          <span className={homeStyles.mobileMenuBrand}>
            <img src={logoSrc} alt="Spotfli" className={homeStyles.mobileLogo} />
          </span>
          <button className={homeStyles.mobileMenuClose} onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        <div className={homeStyles.mobileMenuBody}>
          {/* Profile */}
          {token && (
            <button
              className={homeStyles.mobileProfile}
              onClick={() => { navigate('/accountbilling'); setIsMobileMenuOpen(false); }}
            >
              <img
                src={profileImage}
                alt="Profile"
                className={homeStyles.mobileProfileAvatar}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultProfile;
                }}
              />
              <span className={homeStyles.mobileProfileInfo}>
                <span className={homeStyles.mobileProfileName}>{userName}</span>
                <span className={homeStyles.mobileProfileHint}>{t('sidebar.manage_account')}</span>
              </span>
            </button>
          )}

          {/* Auth */}
          <div className={`${homeStyles.mobileAuthButtons} ${styles.authGrid}`}>
            {token ? (
              <>
                <DashboardShortcuts user={profileUser} variant="mobile" onNavigate={() => setIsMobileMenuOpen(false)} />
                <button onClick={() => { navigate('/accountbilling'); setIsMobileMenuOpen(false); }} className={homeStyles.mobileRegisterBtn}>
                  <FiUser /><span>{t('sidebar.manage_account')}</span>
                </button>
                <button onClick={() => { navigate('/user/bookings'); setIsMobileMenuOpen(false); }} className={homeStyles.mobileRegisterBtn}>
                  <FaCalendarCheck /><span>{t('home.bottom_nav.bookings')}</span>
                </button>
                <button onClick={handleLogout} className={`${homeStyles.mobileLoginBtn} ${homeStyles.mobileLogoutBtn}`}>
                  <FaSignOutAlt /><span>{t('service_selector.log_out')}</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { navigate('/register'); setIsMobileMenuOpen(false); }} className={homeStyles.mobileRegisterBtn}>
                  <FaUserPlus /><span>{t('service_selector.get_started_free')}</span>
                </button>
                <button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }} className={homeStyles.mobileLoginBtn}>
                  <FaSignInAlt /><span>{t('service_selector.login')}</span>
                </button>
              </>
            )}
          </div>

          {/* Hébergement */}
          <div className={homeStyles.mobileSection}>
            <h3 className={homeStyles.mobileNavTitle}>
              <FaBed /> {t('home.hero.intent_stay')}
            </h3>
            <div className={homeStyles.mobileServices}>
              {allServices.filter((s) => s.tab === 'stay').map((service) => (
                <button key={service.id} className={homeStyles.mobileServiceLink}
                  onClick={() => { handleServiceSelect(service.route); }}>
                  <span className={homeStyles.mobileServiceIcon} style={{ color: service.accent, background: `${service.accent}14` }}>
                    {service.icon}
                  </span>
                  <span className={homeStyles.mobileServiceTitle}>{service.title}</span>
                  <FaChevronRight className={homeStyles.mobileServiceArrow} />
                </button>
              ))}
            </div>
          </div>

          {/* Prestations */}
          <div className={homeStyles.mobileSection}>
            <h3 className={homeStyles.mobileNavTitle}>
              <FaTools /> {t('home.hero.intent_services')}
            </h3>
            <div className={homeStyles.mobileServices}>
              {allServices.filter((s) => s.tab === 'services').map((service) => (
                <button key={service.id} className={homeStyles.mobileServiceLink}
                  onClick={() => { handleServiceSelect(service.route); }}>
                  <span className={homeStyles.mobileServiceIcon} style={{ color: service.accent, background: `${service.accent}14` }}>
                    {service.icon}
                  </span>
                  <span className={homeStyles.mobileServiceTitle}>{service.title}</span>
                  <FaChevronRight className={homeStyles.mobileServiceArrow} />
                </button>
              ))}
            </div>
          </div>

          {/* Langue + thème */}
          <div className={homeStyles.mobileMenuFooter}>
            <span className={homeStyles.mobileFooterLabel}><FaCompass /> {t('options.services')}</span>
            <div className={homeStyles.mobileFooterActions}>
              <button
                className={homeStyles.themeToggle}
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? <FaSun /> : <FaMoon />}
              </button>
              <div className={homeStyles.language}><LanguageSelector /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && createPortal(
        <div className={styles['premium-logout-overlay']} onClick={(e) => { if (e.target === e.currentTarget) setShowLogoutConfirm(false); }}>
          <div className={styles['premium-logout-modal']}>
            <div className={styles['premium-logout-icon']}>
              <FaSignOutAlt size={22} />
            </div>
            <h3 className={styles['premium-logout-title']}>{t('sidebar.logout')}?</h3>
            <p className={styles['premium-logout-desc']}>{t('sidebar.logout_confirm', 'Are you sure you want to log out of your account?')}</p>
            <div className={styles['premium-logout-actions']}>
              <button className={styles['premium-logout-cancel']} onClick={() => setShowLogoutConfirm(false)}>
                {t('sidebar.cancel', 'Cancel')}
              </button>
              <button className={styles['premium-logout-confirm']} onClick={handleLogoutConfirm}>
                <FaSignOutAlt size={14} />
                {t('sidebar.logout')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PremiumHeader;
